import React, { useState, useMemo } from 'react';
import { Select, Input, Checkbox, Typography, Badge } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import './CustomSelectComponent.scss';

const { Text } = Typography;

// Type definitions
export interface SelectOption {
    value: string;
    label: string;
    count?: number;
    icon?: React.ReactNode;
}

export interface CustomSelectComponentProps {
    options: SelectOption[] | any;
    values?: string | string[];
    onChange: (value: string | string[], selectedOptions?: SelectOption[]) => void;
    isMultiple?: boolean;
    search?: boolean;
    placeholder?: string;
    icon?: React.ReactNode;
    label?: string;
    className?: string;
    disabled?: boolean;
    allowClear?: boolean;
}

const CustomSelectComponent: React.FC<CustomSelectComponentProps> = ({
                                                                         options = [],
                                                                         values,
                                                                         onChange,
                                                                         isMultiple = false,
                                                                         search = true,
                                                                         placeholder = "Select profession",
                                                                         icon = null,
                                                                         label = "Profession",
                                                                         className = "",
                                                                         disabled = false,
                                                                         allowClear = true,
                                                                     }) => {
    const [searchText, setSearchText] = useState<string>('');
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

    // Filter options based on search text
    const filteredOptions = useMemo(() => {
        if (!search || !searchText.trim()) {
            return options;
        }
        return options.filter((option:any) =>
            option.label.toLowerCase().includes(searchText.toLowerCase()) ||
            option.value.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [options, searchText, search]);

    // Get selected count for display
    const selectedCount = useMemo(() => {
        if (!values) return 0;
        if (Array.isArray(values)) return values.length;
        return values ? 1 : 0;
    }, [values]);

    // Handle selection change
    const handleChange = (selectedValues: string | string[]) => {
        const selectedOptions = options.filter((option:any) => {
            if (Array.isArray(selectedValues)) {
                return selectedValues.includes(option.value);
            }
            return option.value === selectedValues;
        });

        onChange(selectedValues, selectedOptions);
    };

    // Handle checkbox change in multiple mode
    const handleCheckboxChange = (optionValue: string, checked: boolean) => {
        if (!isMultiple) return;

        const currentValues = Array.isArray(values) ? values : (values ? [values] : []);
        let newValues: string[];

        if (checked) {
            newValues = [...currentValues, optionValue];
        } else {
            newValues = currentValues.filter(val => val !== optionValue);
        }

        handleChange(newValues);
    };

    // Handle single select
    const handleSingleSelect = (optionValue: string) => {
        if (isMultiple) return;
        handleChange(optionValue);
        setDropdownVisible(false);
    };

    // Get display text for the selector
    const getDisplayText = () => {
        if (!values) return placeholder;

        if (Array.isArray(values)) {
            if (values.length === 0) return placeholder;
            if (values.length === 1) {
                const option = options.find((opt:any) => opt.value === values[0]);
                return option?.label || values[0];
            }
            return `${values.length} selected`;
        }

        const option = options.find((opt:any) => opt.value === values);
        return option?.label || values;
    };

    // Custom dropdown render
    const dropdownRender = () => (
        <div className="custom-select-dropdown">
            {search && (
                <div className="search-container">
                    <Input
                        size={'large'}
                        prefix={<SearchOutlined />}
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            )}

            <div className="options-container">
                {filteredOptions.length === 0 ? (
                    <div className="no-options">
                        No options found
                    </div>
                ) : (
                    filteredOptions.map((option:any) => {
                        const isSelected = Array.isArray(values)
                            ? values.includes(option.value)
                            : values === option.value;

                        return (
                            <div
                                key={option.value}
                                className={`custom-option ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                    if (isMultiple) {
                                        handleCheckboxChange(option.value, !isSelected);
                                    } else {
                                        handleSingleSelect(option.value);
                                    }
                                }}
                            >
                                <div className="option-content">
                                    {isMultiple && (
                                        <div className="option-checkbox">
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                                            />
                                        </div>
                                    )}
                                    {option.icon && <span className="option-icon">{option.icon}</span>}
                                    <Text className="option-label">{option.label}</Text>
                                </div>

                                {option.count !== undefined && (
                                    <div className="option-count">
                                        <Badge count={option.count} />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <div className={`custom-select-wrapper ${className}`}>
            <Select
                value={values}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                allowClear={allowClear}
                open={dropdownVisible}
                onDropdownVisibleChange={setDropdownVisible}
                dropdownRender={dropdownRender}
                size={'large'}
            >
                {/* This is required for Ant Design Select to work, but we override with dropdownRender */}
                {options.map((option:any) => (
                    <Select.Option key={option.value} value={option.value}>
                        {option.label}
                    </Select.Option>
                ))}
            </Select>

            {/* Custom display for the select box */}
            <div
                className={`custom-select-display ${disabled ? 'disabled' : ''}`}
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && setDropdownVisible(!dropdownVisible)}
                onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setDropdownVisible(!dropdownVisible);
                    }
                }}
            >
                <div className="display-content">
                    <span className="display-icon">{icon}</span>
                    <Text className={`display-text ${values ? 'selected' : 'placeholder'}`}>
                        {getDisplayText()}
                    </Text>
                    {selectedCount > 0 && isMultiple && (
                        <div className="selected-badge">
                            <Badge count={selectedCount} />
                        </div>
                    )}
                </div>
                <DownOutlined className={`dropdown-icon ${dropdownVisible ? 'open' : ''}`} />
            </div>
        </div>
    );
};

export default CustomSelectComponent;